/**
 * <hybridx-form> — progressive enhancement for the HybridX contact form block.
 *
 * The form itself submits through Shopify's native contact endpoint, so everything here is optional
 * polish: a drag-and-drop dropzone that mirrors the hidden file input, a list of chosen files with
 * per-file removal, and a basic size guard. No dependency on the theme's component runtime so the
 * block keeps working even if the module graph changes.
 */

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB per file — matches typical inbox limits.
const ACCEPTED = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];

class HybridxForm extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-file-input]');
    this.dropzone = this.querySelector('[data-dropzone]');
    this.list = this.querySelector('[data-file-list]');

    if (!this.input || !this.dropzone) return;

    // DataTransfer lets us rebuild the input's FileList when a file is removed.
    this.files = [];

    this.input.addEventListener('change', this.#onInputChange);
    this.dropzone.addEventListener('dragover', this.#onDragOver);
    this.dropzone.addEventListener('dragleave', this.#onDragLeave);
    this.dropzone.addEventListener('drop', this.#onDrop);
    this.list?.addEventListener('click', this.#onListClick);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('change', this.#onInputChange);
    this.dropzone?.removeEventListener('dragover', this.#onDragOver);
    this.dropzone?.removeEventListener('dragleave', this.#onDragLeave);
    this.dropzone?.removeEventListener('drop', this.#onDrop);
    this.list?.removeEventListener('click', this.#onListClick);
  }

  #onInputChange = () => {
    this.#addFiles(this.input.files);
  };

  #onDragOver = (event) => {
    event.preventDefault();
    this.dropzone.setAttribute('data-dragging', '');
  };

  #onDragLeave = () => {
    this.dropzone.removeAttribute('data-dragging');
  };

  #onDrop = (event) => {
    event.preventDefault();
    this.dropzone.removeAttribute('data-dragging');
    this.#addFiles(event.dataTransfer?.files);
  };

  #onListClick = (event) => {
    const button = event.target.closest('[data-remove]');
    if (!button) return;

    const index = Number(button.getAttribute('data-remove'));
    this.files.splice(index, 1);
    this.#setError('');
    this.#sync();
  };

  /**
   * @param {FileList | null | undefined} incoming
   */
  #addFiles(incoming) {
    if (!incoming) return;

    // Reset any prior rejection message before validating this batch.
    this.#setError('');

    for (const file of Array.from(incoming)) {
      const validType = ACCEPTED.includes(file.type) || /\.(jpe?g|png|gif|svg)$/i.test(file.name);
      if (!validType) {
        this.#setError(`"${file.name}" is not a supported image format.`);
        continue;
      }

      if (file.size > MAX_FILE_BYTES) {
        this.#setError(`"${file.name}" is larger than 10MB.`);
        continue;
      }

      // Skip duplicates by name + size so re-dropping the same file is a no-op.
      const exists = this.files.some((f) => f.name === file.name && f.size === file.size);
      if (!exists) this.files.push(file);
    }

    this.#sync();
  }

  /** Rewrites the input's FileList from `this.files` and repaints the chip list. */
  #sync() {
    const transfer = new DataTransfer();
    for (const file of this.files) transfer.items.add(file);
    this.input.files = transfer.files;

    if (!this.list) return;

    this.list.replaceChildren();

    this.files.forEach((file, index) => {
      const chip = document.createElement('li');
      chip.className = 'hybridx-form__file';

      const name = document.createElement('span');
      name.className = 'hybridx-form__file-name';
      name.textContent = file.name;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'hybridx-form__file-remove';
      remove.setAttribute('data-remove', String(index));
      remove.setAttribute('aria-label', `Remove ${file.name}`);
      remove.textContent = '×';

      chip.append(name, remove);
      this.list.append(chip);
    });
  }

  /** @param {string} message */
  #setError(message) {
    const target = this.querySelector('[data-file-error]');
    if (target) target.textContent = message;
  }
}

if (!customElements.get('hybridx-form')) {
  customElements.define('hybridx-form', HybridxForm);
}
